import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';

function Incidents({ user }) {
  const [incidents, setIncidents] = useState([]);
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const [newIncident, setNewIncident] = useState({
    incident_date: new Date().toISOString().split('T')[0],
    incident_type: 'panne',
    severity: 'medium',
    description: '',
    location: '',
    vehicle_immobilized: false,
    estimated_repair_cost: ''
  });

  // Pour admin: mise à jour d'incident
  const [updateData, setUpdateData] = useState({
    repair_status: '',
    admin_notes: '',
    estimated_repair_cost: ''
  });

  useEffect(() => {
    fetchIncidents();
  }, []);

  const fetchIncidents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/incidents', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncidents(response.data);
    } catch (error) {
      console.error('Erreur incidents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitIncident = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/incidents', newIncident, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Incident déclaré avec succès!');
      setShowNewForm(false);
      setNewIncident({
        incident_date: new Date().toISOString().split('T')[0],
        incident_type: 'panne',
        severity: 'medium',
        description: '',
        location: '',
        vehicle_immobilized: false,
        estimated_repair_cost: ''
      });
      fetchIncidents();
    } catch (error) {
      console.error('Erreur déclaration:', error);
      alert('Erreur lors de la déclaration de l\'incident.');
    }
  };

  const handleUpdateIncident = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/incidents/${selectedIncident.id}`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Incident mis à jour!');
      setSelectedIncident(null);
      fetchIncidents();
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('Erreur lors de la mise à jour.');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      reported: '#e74c3c',
      in_progress: '#f39c12',
      waiting_parts: '#e67e22',
      repaired: '#27ae60',
      closed: '#95a5a6'
    };
    return colors[status] || '#999';
  };

  const getStatusLabel = (status) => {
    const labels = {
      reported: '🔴 Déclaré',
      in_progress: '🟡 En cours',
      waiting_parts: '🟠 Attente pièces',
      repaired: '🟢 Réparé',
      closed: '⚪ Clôturé'
    };
    return labels[status] || status;
  };

  const getTypeLabel = (type) => {
    const labels = {
      panne: '🔧 Panne mécanique',
      accident: '💥 Accident',
      crevaison: '🛞 Crevaison',
      probleme_mecanique: '🔧 Problème mécanique',
      probleme_electrique: '⚡ Problème électrique',
      carrosserie: '🚗 Carrosserie',
      autre: '❓ Autre'
    };
    return labels[type] || type;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      low: '#27ae60',
      medium: '#f39c12',
      high: '#e67e22',
      critical: '#e74c3c'
    };
    return colors[severity] || '#999';
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">Chargement des incidents...</div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: 0 }}>🚨 Incidents Véhicule</h2>
          {user.role === 'driver' && (
            <button
              onClick={() => {
                setShowNewForm(true);
                setSelectedIncident(null);
              }}
              style={{
                padding: '0.7rem 1.5rem',
                background: '#e74c3c',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              🚨 Déclarer un incident
            </button>
          )}
        </div>

        {showNewForm ? (
          <div style={{ background: '#f8f9fa', padding: '2rem', borderRadius: '12px', marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1.5rem' }}>Déclarer un nouvel incident</h3>
            <form onSubmit={handleSubmitIncident}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Date de l'incident</label>
                  <input
                    type="date"
                    value={newIncident.incident_date}
                    onChange={(e) => setNewIncident({ ...newIncident, incident_date: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Type d'incident</label>
                  <select
                    value={newIncident.incident_type}
                    onChange={(e) => setNewIncident({ ...newIncident, incident_type: e.target.value })}
                    required
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="panne">🔧 Panne</option>
                    <option value="accident">💥 Accident</option>
                    <option value="crevaison">🛞 Crevaison</option>
                    <option value="probleme_mecanique">🔧 Problème mécanique</option>
                    <option value="probleme_electrique">⚡ Problème électrique</option>
                    <option value="carrosserie">🚗 Carrosserie</option>
                    <option value="autre">❓ Autre</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Gravité</label>
                  <select
                    value={newIncident.severity}
                    onChange={(e) => setNewIncident({ ...newIncident, severity: e.target.value })}
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  >
                    <option value="low">Faible</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Élevée</option>
                    <option value="critical">Critique</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Localisation</label>
                  <input
                    type="text"
                    value={newIncident.location}
                    onChange={(e) => setNewIncident({ ...newIncident, location: e.target.value })}
                    placeholder="Ex: A1 Paris-Lille"
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Coût estimé (€)</label>
                  <input
                    type="number"
                    value={newIncident.estimated_repair_cost}
                    onChange={(e) => setNewIncident({ ...newIncident, estimated_repair_cost: e.target.value })}
                    placeholder="Optionnel"
                    style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                  />
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1.8rem' }}>
                    <input
                      type="checkbox"
                      checked={newIncident.vehicle_immobilized}
                      onChange={(e) => setNewIncident({ ...newIncident, vehicle_immobilized: e.target.checked })}
                    />
                    <span style={{ fontWeight: 'bold' }}>Véhicule immobilisé</span>
                  </label>
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Description détaillée</label>
                <textarea
                  value={newIncident.description}
                  onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                  required
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.7rem',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                    fontFamily: 'inherit',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button
                  type="submit"
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#27ae60',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  📤 Déclarer
                </button>
                <button
                  type="button"
                  onClick={() => setShowNewForm(false)}
                  style={{
                    padding: '0.7rem 1.5rem',
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer'
                  }}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        ) : null}

        {/* Liste des incidents */}
        {incidents.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem', color: '#999' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚨</div>
            <p>Aucun incident déclaré</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '1.5rem' }}>
            {incidents.map(incident => (
              <div
                key={incident.id}
                style={{
                  padding: '1.5rem',
                  background: 'white',
                  border: `2px solid ${getStatusColor(incident.repair_status)}`,
                  borderRadius: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onClick={() => setSelectedIncident(incident)}
                onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)'}
                onMouseLeave={(e) => e.currentTarget.style.boxShadow = 'none'}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <span style={{
                    padding: '0.4rem 1rem',
                    background: getStatusColor(incident.repair_status),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold'
                  }}>
                    {getStatusLabel(incident.repair_status)}
                  </span>
                  <span style={{
                    padding: '0.4rem 1rem',
                    background: getSeverityColor(incident.severity),
                    color: 'white',
                    borderRadius: '20px',
                    fontSize: '0.8rem'
                  }}>
                    {incident.severity}
                  </span>
                </div>

                <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                  {getTypeLabel(incident.incident_type)}
                </h3>

                {incident.driver_name && (
                  <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.3rem' }}>
                    👤 {incident.driver_name} • 🚛 {incident.vehicle_plate}
                  </div>
                )}

                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.8rem' }}>
                  📅 {format(new Date(incident.incident_date), 'dd/MM/yyyy')}
                  {incident.location && ` • 📍 ${incident.location}`}
                </div>

                <div style={{ fontSize: '0.9rem', color: '#333', marginBottom: '0.8rem', lineHeight: '1.4' }}>
                  {incident.description.substring(0, 100)}{incident.description.length > 100 ? '...' : ''}
                </div>

                {incident.vehicle_immobilized === 1 && (
                  <div style={{
                    padding: '0.5rem',
                    background: '#ffebee',
                    color: '#c62828',
                    borderRadius: '6px',
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    textAlign: 'center',
                    marginBottom: '0.8rem'
                  }}>
                    ⚠️ Véhicule immobilisé
                  </div>
                )}

                {incident.estimated_repair_cost && (
                  <div style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#e67e22' }}>
                    💰 Coût: {parseFloat(incident.estimated_repair_cost).toFixed(2)}€
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Modal détails incident (admin peut modifier) */}
        {selectedIncident && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={() => setSelectedIncident(null)}
          >
            <div
              style={{
                background: 'white',
                padding: '2rem',
                borderRadius: '12px',
                maxWidth: '600px',
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginBottom: '1.5rem' }}>Détails de l'incident #{selectedIncident.id}</h2>

              <div style={{ marginBottom: '1rem' }}>
                <strong>Type:</strong> {getTypeLabel(selectedIncident.incident_type)}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Date:</strong> {format(new Date(selectedIncident.incident_date), 'dd/MM/yyyy')}
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Gravité:</strong> <span style={{ color: getSeverityColor(selectedIncident.severity) }}>{selectedIncident.severity}</span>
              </div>
              <div style={{ marginBottom: '1rem' }}>
                <strong>Statut:</strong> {getStatusLabel(selectedIncident.repair_status)}
              </div>
              {selectedIncident.driver_name && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Chauffeur:</strong> {selectedIncident.driver_name} ({selectedIncident.vehicle_plate})
                </div>
              )}
              {selectedIncident.location && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Localisation:</strong> {selectedIncident.location}
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <strong>Véhicule immobilisé:</strong> {selectedIncident.vehicle_immobilized ? 'Oui ⚠️' : 'Non'}
              </div>
              {selectedIncident.estimated_repair_cost && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Coût estimé:</strong> {parseFloat(selectedIncident.estimated_repair_cost).toFixed(2)}€
                </div>
              )}
              <div style={{ marginBottom: '1rem' }}>
                <strong>Description:</strong>
                <p style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '6px', marginTop: '0.5rem' }}>
                  {selectedIncident.description}
                </p>
              </div>
              {selectedIncident.admin_notes && (
                <div style={{ marginBottom: '1rem' }}>
                  <strong>Notes admin:</strong>
                  <p style={{ background: '#fff3cd', padding: '1rem', borderRadius: '6px', marginTop: '0.5rem' }}>
                    {selectedIncident.admin_notes}
                  </p>
                </div>
              )}

              {(user.role === 'admin' || user.role === 'manager') && (
                <form onSubmit={handleUpdateIncident} style={{ marginTop: '2rem', borderTop: '2px solid #e0e0e0', paddingTop: '1.5rem' }}>
                  <h3 style={{ marginBottom: '1rem' }}>Mise à jour (Admin)</h3>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Statut</label>
                    <select
                      value={updateData.repair_status}
                      onChange={(e) => setUpdateData({ ...updateData, repair_status: e.target.value })}
                      style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    >
                      <option value="">-- Choisir un statut --</option>
                      <option value="reported">Déclaré</option>
                      <option value="in_progress">En cours</option>
                      <option value="waiting_parts">Attente pièces</option>
                      <option value="repaired">Réparé</option>
                      <option value="closed">Clôturé</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Notes admin</label>
                    <textarea
                      value={updateData.admin_notes}
                      onChange={(e) => setUpdateData({ ...updateData, admin_notes: e.target.value })}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.7rem',
                        borderRadius: '6px',
                        border: '1px solid #ddd',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Coût réel (€)</label>
                    <input
                      type="number"
                      value={updateData.estimated_repair_cost}
                      onChange={(e) => setUpdateData({ ...updateData, estimated_repair_cost: e.target.value })}
                      style={{ width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #ddd' }}
                    />
                  </div>

                  <button
                    type="submit"
                    style={{
                      padding: '0.7rem 1.5rem',
                      background: '#27ae60',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    💾 Mettre à jour
                  </button>
                </form>
              )}

              <button
                onClick={() => setSelectedIncident(null)}
                style={{
                  marginTop: '1.5rem',
                  padding: '0.7rem 1.5rem',
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  width: '100%'
                }}
              >
                Fermer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Incidents;
